// Choosing and ordering a saved list's columns.
//
// Up/down buttons rather than drag. No drag library is present and one should
// not be added for this: a hand-rolled HTML5 drag implementation needs a
// keyboard fallback anyway, and that fallback *is* a pair of buttons. So the
// buttons are the whole feature rather than a consolation for people who cannot
// drag.

/**
 * Wires the column picker.
 *
 * @param {object} options
 * @param {Element} options.wurzel the picker element
 * @param {function} options.onAenderung called whenever the selection changes
 * @returns {{spalten: function}|null}
 */
export function initSpaltenPicker({ wurzel, onAenderung }) {
  if (!wurzel) return null;

  const gewaehlt = wurzel.querySelector('[data-rolle="gewaehlt"]');
  const hinzu = wurzel.querySelector('[data-rolle="hinzu"]');
  if (!gewaehlt) return null;

  const melde = () => {
    if (typeof onAenderung === 'function') onAenderung();
  };

  // The chosen keys, in display order - read off the DOM rather than held
  // beside it, so the order shown and the order sent cannot drift.
  function spalten() {
    return [...gewaehlt.children].map((el) => el.dataset.spalte).filter(Boolean);
  }

  // Which options are still on offer. Recomputed after every change so a column
  // already chosen cannot be added twice.
  function angebotAktualisieren() {
    if (!hinzu) return;

    const genommen = new Set(spalten());
    [...hinzu.options].forEach((option) => {
      if (!option.value) return;
      option.hidden = genommen.has(option.value);
    });
    hinzu.value = '';
  }

  wurzel.addEventListener('click', (event) => {
    const button = event.target.closest('button');
    if (!button || !wurzel.contains(button)) return;

    const zeile = button.closest('[data-spalte]');
    if (!zeile) return;

    event.preventDefault();

    const rolle = button.dataset.rolle;

    if (rolle === 'hoch' && zeile.previousElementSibling) {
      zeile.parentNode.insertBefore(zeile, zeile.previousElementSibling);
    } else if (rolle === 'runter' && zeile.nextElementSibling) {
      zeile.parentNode.insertBefore(zeile.nextElementSibling, zeile);
    } else if (rolle === 'weg') {
      // The last column is not removable: a list with no columns renders an
      // empty table with a pager under it, which reads as a bug. The model
      // refuses it too.
      if (gewaehlt.children.length <= 1) return;
      zeile.remove();
    } else {
      return;
    }

    angebotAktualisieren();
    melde();
  });

  if (hinzu) {
    hinzu.addEventListener('change', () => {
      const key = hinzu.value;
      if (!key) return;

      const option = [...hinzu.options].find((o) => o.value === key);
      const label = option ? option.textContent : key;

      gewaehlt.insertAdjacentHTML(
        'beforeend',
        [
          `<div class="flex items-center gap-1 text-sm p-1 rounded bg-base-200" data-spalte="${key}">`,
          `<span class="flex-1 truncate" title="${escapeAttr(label)}">${escapeHtml(label)}</span>`,
          `<button type="button" class="btn btn-ghost btn-xs btn-square" data-rolle="hoch" aria-label="${escapeAttr(label)} nach oben"><i class="fa-solid fa-chevron-up" aria-hidden="true"></i></button>`,
          `<button type="button" class="btn btn-ghost btn-xs btn-square" data-rolle="runter" aria-label="${escapeAttr(label)} nach unten"><i class="fa-solid fa-chevron-down" aria-hidden="true"></i></button>`,
          `<button type="button" class="btn btn-ghost btn-xs btn-square text-error" data-rolle="weg" aria-label="${escapeAttr(label)} entfernen"><i class="fa-solid fa-xmark" aria-hidden="true"></i></button>`,
          '</div>'
        ].join('')
      );

      angebotAktualisieren();
      melde();
    });
  }

  angebotAktualisieren();

  return { spalten };
}

function escapeHtml(text) {
  return String(text).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function escapeAttr(text) {
  return escapeHtml(text).replace(/"/g, '&quot;');
}
